export const SUPPORTED_LOCALES = [
  "en", // English
  "de", // Deutsch
  "fr", // Français
  "es", // Español
  "it", // Italiano
  "pt", // Português
  "sk", // Slovenčina
  "cs", // Čeština
  "nl", // Nederlands
  "sv", // Svenska
  "pl", // Polski
] as const;

export const SUPPORTED_PLATFORMS = [
  "goodbarber",
  "elementor",
  "shopify",
  "wordpress",
  "webflow",
  "wix",
  "squarespace",
] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];
export type Platform = (typeof SUPPORTED_PLATFORMS)[number];

export interface LandingPageContent {
  nav: {
    home: string;
  };
  meta: {
    title: string;
    description: string;
    ogTitle?: string;
    ogDescription?: string;
  };
  hero: {
    title: string;
    subtitle: string;
    ctaPrimary: string;
    ctaSecondary: string;
    trustIndicators: {
      customizable: string;
      whiteLabel: string;
      customFeatures: string;
    };
    featuresShowcase: Array<{
      icon: string;
      title: string;
      description: string;
    }>;
  };
  benefits: {
    title: string;
    subtitle: string;
    items: Array<{
      id: string;
      title: string;
      description: string;
    }>;
  };
  integration: {
    title: string;
    description: string;
    platformName: string;
    methods: {
      javascript: string;
      html: string;
    };
    diagram: {
      appLabel: string;
      widgetLabel: string;
      brandLabel: string;
    };
  };
  features: {
    title: string;
    items: Array<{
      id: string;
      icon: string;
      title: string;
      description: string;
    }>;
  };
  pricing: {
    title: string;
    subtitle: string;
    headerTitle: string;
    featuresTitle: string;
    summaryText: string;
    pricePrefix: string;
    mainPricing: {
      setup: {
        icon: string;
        title: string;
        priceFrom: string;
        priceLabel: string;
        description: string;
        features: string[];
      };
      monthly: {
        icon: string;
        title: string;
        priceFrom: string;
        priceLabel: string;
        description: string;
        features: string[];
      };
    };
    customDev: {
      icon: string;
      badge: string;
      title: string;
      price?: string;
      description: string;
      examples: string[];
    };
    note: string;
  };
  howItWorks: {
    title: string;
    steps: Array<{
      id: number;
      title: string;
      description: string;
    }>;
  };
  finalCta: {
    title: string;
    ctaPrimary: string;
    ctaSecondary: string;
    footerNote: string;
  };
}

export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  es: "Español",
  it: "Italiano",
  pt: "Português",
  sk: "Slovenčina",
  cs: "Čeština",
  nl: "Nederlands",
  sv: "Svenska",
  pl: "Polski",
};

export const PLATFORM_NAMES: Record<Platform, string> = {
  goodbarber: "GoodBarber",
  elementor: "Elementor Pro",
  shopify: "Shopify",
  wordpress: "WordPress",
  webflow: "Webflow",
  wix: "Wix",
  squarespace: "Squarespace",
};

export interface HomeContent {
  nav: {
    home: string;
  };
  meta: {
    title: string;
    description: string;
  };
  hero: {
    title: {
      part1: string;
      part2: string;
      part3: string;
    };
    subtitle: string;
    ctaButton: string;
    trustIndicators: {
      customizable: string;
      whiteLabel: string;
      customFeatures: string;
    };
    flowDiagram: {
      widget: string;
      customUI: string;
      fullControl: string;
      customLogic: string;
      payments: string;
      emailSms: string;
      calendar: string;
      analytics: string;
      adminPanel: string;
      whiteLabel: string;
      noBranding: string;
      yourDesign: string;
      fullControlLabel: string;
    };
  };
  features: {
    title: {
      part1: string;
      part2: string;
    };
    subtitle: string;
    items: Array<{
      id: string;
      title: string;
      description: string;
    }>;
    bottomCta: {
      title: {
        part1: string;
        part2: string;
      };
      description: string;
    };
  };
  howItWorks: {
    title: string;
    subtitle: string;
    steps: Array<{
      id: number;
      title: string;
      description: string;
    }>;
    bottomMessage: string;
  };
  widgetShowcase: {
    title: {
      part1: string;
      part2: string;
    };
    subtitle: string;
    comparison: {
      standard: string;
      flexibooking: string;
      selectDate: string;
      selectTime: string;
      yourName: string;
      continue: string;
      poweredBy: string;
      yourBrand: string;
      foreignLogo: string;
      fixedDesign: string;
      genericColors: string;
      customDesign: string;
      yourColors: string;
    };
    customFeatures: {
      title: string;
      items: Array<{
        id: string;
        title: string;
        description: string;
      }>;
      bottomHighlight: {
        title: string;
        description: string;
      };
    };
  };
  integrations: {
    title: string;
    subtitle: string;
    items: {
      goodbarber: {
        name: string;
        description: string;
        ctaText: string;
      };
      elementor: {
        name: string;
        description: string;
        ctaText: string;
      };
      customCode: {
        name: string;
        description: string;
        code: string;
        ctaText: string;
      };
    };
  };
  cta: {
    title: string;
    subtitle: string;
    button: string;
    trustIndicators: {
      customizable: {
        value: string;
        label: string;
      };
      delivery: {
        value: string;
        label: string;
      };
      branding: {
        value: string;
        label: string;
      };
    };
    contact: {
      title: string;
      description: string;
      email: string;
    };
  };
}
